/* Modal is a skin of the ReactModal project (http://reactcommunity.org/react-modal/). */

import React, { forwardRef } from "react";
import { bool, func, node, string } from "prop-types";

import { STREETEASY_SITE_CONTENT_SELECTOR } from "../constants/streeteasySiteContentSelector";
import { ModalBase, ModalGlobalStyle } from "./styled";

export const overlayClassName = "se-design-system-modal-overlay";

const Modal = forwardRef(
  (
    {
      ariaDescribedBy,
      ariaHiddenSelector,
      ariaLabelledBy,
      children,
      className,
      isOpen,
      onRequestClose,
      ...passThroughProps
    },
    ref
  ) => {
    ModalBase.setAppElement(ariaHiddenSelector);

    return (
      <ModalBase
        ref={ref}
        aria={{ describedby: ariaDescribedBy, labelledby: ariaLabelledBy }}
        className={className}
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        overlayClassName={overlayClassName}
        {...passThroughProps}
      >
        <ModalGlobalStyle ariaHiddenSelector={ariaHiddenSelector} />
        {children}
      </ModalBase>
    );
  }
);

Modal.displayName = "Modal";

Modal.propTypes = {
  /**
   * The ID of the element which describes the modal's comment. Using aria attributes
   * to identify especially meaningful content is considered an accessibility
   * best practice. If the modal does not have descriptive text this prop can
   * be skipped, or a visually-hidden description can be provided.
   */
  ariaDescribedBy: string,
  /**
   * A selector identifying the page element or elements that should receive
   * the aria-hidden attribute when the modal is open. All page content
   * besides the modal should be decorated with aria-hidden when the modal is open,
   * including the site header and footer.
   *
   * ```html
   * <body>
   *   <div id="aria-hidden-when-modal-open">
   *     <header>Header Content</header>
   *     <main>Main Content<main>
   *     <footer>Footer Content</footer>
   *   </div>
   *   <div class="ReactModalPortal"></div>
   * </body>
   *
   * To add aria-hidden to non-modal content in the above snippet, we would
   * pass the value "#content" to the ariaHiddenSelector prop.
   *
   * We provide a default value for this prop that matches the selector of
   * a container element in all Streeteasy layout files. Please do not provide
   * this prop unless you are sure you know what you are doing.
   * ```
   * */
  ariaHiddenSelector: string,
  /**
   * The ID of the element which describes the modal's label (heading). Using aria
   * attributes to identify especially meaningful content is considered an accessibility
   * best practice. If the modal does not have a label or heading, this prop can
   * be skipped, or a visually-hidden heading can be provided.
   */
  ariaLabelledBy: string,
  className: string,
  /**
   * The Modal's content
   * */
  children: node.isRequired,
  /**
   * Determines if Modal is open or not
   */
  isOpen: bool.isRequired,
  /**
   * A function called when the modal's overlay is clicked.
   */
  onRequestClose: func.isRequired
};

Modal.defaultProps = {
  ariaDescribedBy: null,
  ariaLabelledBy: null,
  ariaHiddenSelector: STREETEASY_SITE_CONTENT_SELECTOR,
  className: ""
};

export default Modal;
